import React from "react";

// External
import styled from "styled-components";

// Internal
import {
  breakpoints,
  colors,
  fontFamily,
  sectionRhythm,
  spacing,
} from "../../../theme";

// Assets
import { logoImage } from "../../utils/cdn";

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing(3)};
  width: 94%;
  max-width: 1040px;
  margin: 0 auto;
  padding-bottom: ${sectionRhythm.paddingY.mobile}px;
  box-sizing: border-box;

  @media only screen and (min-width: ${sectionRhythm.breakpoint}) {
    padding-bottom: ${sectionRhythm.paddingY.desktop}px;
  }

  @media only screen and (min-width: ${breakpoints.tablet}) {
    gap: ${spacing(5)};
  }

  @media only screen and (min-width: ${breakpoints.desktop}) {
    gap: ${spacing(6)};
  }
`;

const Title = styled.p`
  flex-shrink: 0;
  margin: 0;
  font-family: ${fontFamily.bold};
  font-size: 16px;
  line-height: 1.2;
  color: ${colors.grey.darkMedium};
  white-space: nowrap;

  @media only screen and (min-width: 420px) {
    font-size: 20px;
  }

  @media only screen and (min-width: 576px) {
    font-size: 24px;
  }

  @media only screen and (min-width: ${breakpoints.tablet}) {
    font-size: 30px;
  }

  @media only screen and (min-width: ${breakpoints.desktop}) {
    font-size: 36px;
  }
`;

const MarqueeWrapper = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  overflow: clip;
  /* Soft fade where logos enter/exit so they don't collide with the title or screen edge */
  mask-image: linear-gradient(
    to right,
    transparent,
    black 1.5rem,
    black calc(100% - 1.5rem),
    transparent
  );

  @media only screen and (min-width: ${breakpoints.tablet}) {
    mask-image: linear-gradient(
      to right,
      transparent,
      black 2.5rem,
      black calc(100% - 2.5rem),
      transparent
    );
  }
`;

const MarqueeTrack = styled.div`
  display: flex;
  width: max-content;
  align-items: center;
  gap: ${spacing(8)};
  padding-left: ${spacing(8)};
  animation: trusted-by-marquee 28s linear infinite;

  @media only screen and (min-width: ${breakpoints.tablet}) {
    gap: ${spacing(10)};
    padding-left: ${spacing(10)};
    animation-duration: 32s;
  }

  @media only screen and (min-width: ${breakpoints.desktop}) {
    gap: ${spacing(12)};
    padding-left: ${spacing(12)};
    animation-duration: 36s;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  /* translateX(-50%) assumes exactly two identical logo sets */
  @keyframes trusted-by-marquee {
    to {
      transform: translateX(-50%);
    }
  }
`;

const Logo = styled.div`
  height: 28px;
  display: flex;
  align-items: center;
  flex-shrink: 0;

  @media only screen and (min-width: ${breakpoints.tablet}) {
    height: 36px;
  }

  @media only screen and (min-width: ${breakpoints.desktop}) {
    height: 40px;
  }

  img {
    height: 100%;
    width: auto;
    object-fit: contain;
  }
`;

const MeltwaterLogo = styled(Logo)`
  height: 3.5rem;
  width: 7rem;

  @media only screen and (min-width: ${breakpoints.tablet}) {
    height: 4.5rem;
    width: 9rem;
  }

  @media only screen and (min-width: ${breakpoints.desktop}) {
    height: 5rem;
    width: 10rem;
  }

  img {
    width: 100%;
    object-fit: cover;
  }
`;

const TuringLogo = styled(Logo)`
  width: 112px;

  @media only screen and (min-width: ${breakpoints.tablet}) {
    width: 144px;
  }

  @media only screen and (min-width: ${breakpoints.desktop}) {
    width: 160px;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const logos = [
  {
    id: "shakespeers",
    src: logoImage("dark/Shakespeers"),
    alt: "ShakesPeers",
    type: "default",
  },
  {
    id: "condexo",
    src: logoImage("dark/Condexo"),
    alt: "Condexo",
    type: "default",
  },
  {
    id: "meltwater",
    src: logoImage("dark/Meltwater"),
    alt: "Meltwater",
    type: "meltwater",
  },
  {
    id: "jiffy",
    src: logoImage("dark/Jiffy"),
    alt: "Jiffy",
    type: "default",
  },
  {
    id: "akasa-air",
    src: logoImage("dark/AkasaAir"),
    alt: "Akasa Air",
    type: "default",
  },
  {
    id: "toptal",
    src: logoImage("dark/Toptal"),
    alt: "Toptal",
    type: "default",
  },
  {
    id: "turing",
    src: logoImage("dark/Turing"),
    alt: "Turing",
    type: "turing",
  },
  {
    id: "andela",
    src: logoImage("dark/Andela"),
    alt: "Andela",
    type: "default",
  },
];

const logoWrappers = {
  meltwater: MeltwaterLogo,
  turing: TuringLogo,
};

const logoSizes = {
  meltwater: { width: 160, height: 80 },
  turing: { width: 160, height: 80 },
  default: { width: 273, height: 80 },
};

const LogoItem = ({ logo, ...props }) => {
  const Wrapper = logoWrappers[logo.type] || Logo;
  const { width, height } = logoSizes[logo.type] || logoSizes.default;

  return (
    <Wrapper {...props}>
      <img
        src={logo.src}
        alt={logo.alt}
        width={width}
        height={height}
        loading="lazy"
        decoding="async"
      />
    </Wrapper>
  );
};

const TrustedBySection = () => {
  return (
    <Row aria-label="Trusted by">
      <Title>Trusted by</Title>
      <MarqueeWrapper>
        <MarqueeTrack>
          {logos.map((logo) => (
            <LogoItem key={logo.id} logo={logo} />
          ))}
          {logos.map((logo) => (
            <LogoItem key={`${logo.id}-dup`} logo={logo} aria-hidden="true" />
          ))}
        </MarqueeTrack>
      </MarqueeWrapper>
    </Row>
  );
};

export default TrustedBySection;
